import React, { useState, useEffect } from 'react';
import { X, History, Plus } from 'lucide-react';
import api from '../services/api';

export default function LedgerModal({ tenantId, employee, onClose }) {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustment, setAdjustment] = useState({ days: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tenants/${tenantId}/employees/${employee.id}/ledger`);
      setLedger(res.data.data);
    } catch (err) {
      alert('Failed to fetch ledger history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, [tenantId, employee.id]);

  const handleManualAdjustment = async (e) => {
    e.preventDefault();
    if (!adjustment.days || !adjustment.reason) {
      alert('Please fill out both days and reason');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/tenants/${tenantId}/employees/${employee.id}/ledger`, adjustment);
      setAdjustment({ days: '', reason: '' });
      fetchLedger();
      // We don't close the modal so they can see the new entry
    } catch (err) {
      alert('Failed to post adjustment: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const getBadgeColor = (type) => {
    switch (type) {
      case 'ACCRUAL':
      case 'REVERSAL':
        return 'var(--primary-50)';
      case 'DEDUCTION':
      case 'FORFEITURE':
        return 'var(--accent-rose)';
      case 'MANUAL_ADJUSTMENT':
        return 'var(--accent-amber)';
      default:
        return 'var(--bg-secondary)';
    }
  };

  const getBadgeTextColor = (type) => {
    switch (type) {
      case 'ACCRUAL':
      case 'REVERSAL':
        return 'var(--primary-600)';
      case 'DEDUCTION':
      case 'FORFEITURE':
        return '#9f1239'; // dark rose
      case 'MANUAL_ADJUSTMENT':
        return '#92400e'; // dark amber
      default:
        return 'var(--text-primary)';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '700px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', margin: 0 }}>
              <History size={20} color="var(--primary-500)" />
              Ledger Audit: {employee.name}
            </h2>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Current Balance: <strong style={{ color: 'var(--text-primary)' }}>{employee.currentBalance} Days</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Manual Adjustment Form */}
        <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem' }}>Post Manual Adjustment</h4>
          <form onSubmit={handleManualAdjustment} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
              <label className="form-label">Days (+ or -)</label>
              <input type="number" step="0.5" className="form-input" required value={adjustment.days} onChange={e => setAdjustment({...adjustment, days: e.target.value})} placeholder="e.g. 1.5 or -1" />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: 3 }}>
              <label className="form-label">Reason</label>
              <input type="text" className="form-input" required value={adjustment.reason} onChange={e => setAdjustment({...adjustment, reason: e.target.value})} placeholder="e.g. Weekend overtime compensation" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Plus size={16} /> Add
            </button>
          </form>
        </div>

        {/* Audit Log Table */}
        {loading ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>Loading ledger...</p>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '8px 12px' }}>Date</th>
                  <th style={{ padding: '8px 12px' }}>Type</th>
                  <th style={{ padding: '8px 12px' }}>Days</th>
                  <th style={{ padding: '8px 12px' }}>Reason</th>
                  <th style={{ padding: '8px 12px' }}>Actor</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>
                      {new Date(entry.timestamp).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ 
                        background: getBadgeColor(entry.type), 
                        color: getBadgeTextColor(entry.type),
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        {entry.type}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: entry.days >= 0 ? 'var(--primary-600)' : 'var(--accent-rose)' }}>
                      {entry.days > 0 ? '+' : ''}{entry.days}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{entry.reason}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>{entry.actor}</td>
                  </tr>
                ))}
                {ledger.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)' }}>No ledger entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
